import axios, { Axios, AxiosError } from 'axios';
import _ from 'lodash';
import {
  ICompleteAccountDTO,
  IContactDTO,
  ICreateAccountDTO,
  ICreateBranchDTO,
  ICreateCustomerDTO,
  ICreateCustomerPersonDTO,
  ICreateFinancingDTO,
  ICreateFinancingOfferDTO,
  ICreateFinancingPaymentDTO,
  ICreateHirePurchaseDTO,
  ICreateLoanDTO,
  ICreateLoanProductDTO,
  ICreatePassbookSaleDTO,
  ICreateProductDTO,
  ICreateReconciliationDTO,
  ICreateTransactionDTO,
  ICreateUserDTO,
  IDashboardMainMetrics,
  IGetPaymentLinkDTO,
  IInitializeCompanyPaymentDTO,
  ILoginDTO,
  IRecordRevenueDTO,
  IRequestErrorResponse,
  ISetAgentTargetDTO,
  IUpdateAccountDTO,
  IUpdateCompanyDTO,
  IVerifyPaymentDTO,
} from '../resources/interfaces';
import store, { RootState } from '../states';
import { SIGN_OUT } from '../states/slices/userAccountSlice';
import { HIDE_TOP_LOADING, SHOW_TOP_LOADING } from '../states/slices/globalSlice';
import { isDevelopment } from '../utilities/miscFunctions';
import { ACCOUNT_TYPES, CUSTOMER_TYPES, DOWNLOAD_FILE_TYPES, FINANCING_TYPES, PAYMENT_MODES, RANK_AGENTS_BY, RECONCILIATION_STAGES, REPORT_TYPES, STATEMENT_FORMAT, TRANSACTION_TYPES } from '../resources/enums';
import { IChangePasswordDTO, IResetPasswordDTO } from '../views/settings/Password';
import { ICreateCompanyFormData } from '../views/companies/AddCompany';
import { IBranch } from '../components/models/branch/Branch';

const apiUrl = process.env.REACT_APP_API;

export const api = axios.create({
  baseURL: `${apiUrl}/api`,
  timeout: isDevelopment() ? 150000 : 60000,
  withCredentials: false
});

export const testApi = axios.create({
  baseURL: ``,
  timeout: isDevelopment() ? 150000 : 60000,
  withCredentials: false
});

// Add interceptors to instance
api.interceptors.response.use(
  (response) => {
    store.dispatch(HIDE_TOP_LOADING());
    return response;
  },
  (error: AxiosError) => {
    store.dispatch(HIDE_TOP_LOADING());
    const errorStruct: IRequestErrorResponse = {
      message: 'Error',
      status: 500
    };

    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    if (error.response) {
      const errorResponse = error.response;
      errorStruct.status = errorResponse.status;
      const data: Record<any, any> = <any>errorResponse.data;

      switch (errorResponse.status) {
        case 400: // Bad Request
          errorStruct.message = 'Error';
          errorStruct.description = data?.detail;
          // @ts-ignore
          errorStruct.detail = error.response?.data?.detail ?? error?.response?.detail ?? error?.response?.data?.message ?? error?.message;
          break;
        case 401: // Unauthorized
          errorStruct.message = 'Unauthorized';
          errorStruct.description =
            data?.message ?? 'You are unauthorized to perform this operation.';
          // toast.error("You are either unauthorized to perform this operation or your token has expired.", {
          //   autoClose: 3000
          // })
          // @ts-ignore
          errorStruct.detail = error.response?.data?.detail;
          // store.dispatch(SESSION_EXPIRED(false));
          store.dispatch(SIGN_OUT());
          break;
        case 403: // Forbidden
          errorStruct.message = 'Unauthorized';
          errorStruct.description =
            data?.message ?? 'You are unauthorized to perform this operation.';
          // @ts-ignore
          errorStruct.detail = error.response?.data?.detail;
          break;
        case 408: // Bad Request
          errorStruct.message = 'Connection Timeout';
          errorStruct.description = 'Connection timeout. Try again';
          // @ts-ignore
          errorStruct.detail = error.response?.data?.detail;
          break;
        case 422: // Bad Request
          // @ts-ignore
          errorStruct.message = error.response?.data?.message;
          errorStruct.description = 'Validation failed. Try again';
          // @ts-ignore
          errorStruct.detail = error.response?.data?.detail ?? error.response?.data?.message;
          break;
        case 500: // Server Error
          errorStruct.message = 'Request Error';
          errorStruct.description = 'An error occurred serving request. Try again later.';
          // @ts-ignore
          errorStruct.detail = error.response?.data?.detail ?? error.response?.data?.message;
          break;
        default: // System Error
          errorStruct.message = 'System Error';
          // @ts-ignore
          errorStruct.detail = error.response?.data?.message
          break;
      }

      if (!errorStruct.description) errorStruct.description = data?.message;

      return Promise.reject(errorStruct);
    } else {
      // Something happened in setting up the request that triggered an Error
      return error;
    }
  }
);

api.interceptors.request.use((request) => {
  store.dispatch(SHOW_TOP_LOADING());
  // Set bearer token
  const storeState: RootState = store.getState();
  if (!_.isEmpty(storeState?.userAccount)) {
    const token = storeState?.userAccount?.ACCOUNT?.token;

    if (token && request && request.headers) {
      // @ts-ignore
      request.headers = {
        ...request.headers,
        Authorization: 'Bearer ' + token
      };
    }
  }

  return request;
});

// AUTH REQUESTS
export function loginWithPassword(payload: ILoginDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/auth/login`, payload)
      .then((response: any) => {
        const token = response?.headers?.authorization?.split(' ')[1];
        response.data.data.token = token;
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function activateUser(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/users/activate`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function adminActivateUser(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/users/${id}/activate`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function deactivateUser(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/users/${id}/deactivate`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function getUserProfile(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/users/profile`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getUsers(
  query?: string,
  companyId = "",
  limit?: number,
  branchId?: string,
  isActive?: boolean
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/users?search=${query}&company_id=${companyId}&limit=${limit}&branch_id=${branchId}&is_active=${isActive}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getUserById(id = ""): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/users/${id}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getUserMetrics(id?: string, from?: string, to?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/users/metrics?user_id=${id}&from=${from}&to=${to}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getUserActivitySummary(id?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/users/activity-summary?user_id=${id}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getUserReconciliationOutstanding(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/users/${id}/recon-outstanding`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getReconciliations(
  query?: string,
  limit?: number,
  status?: RECONCILIATION_STAGES,
  agentId?: string,
  from?: string,
  to?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/reconciliations?search=${query}&limit=${limit}&status=${status}&agentId=${agentId}&from=${from}&to=${to}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createUser(payload: ICreateUserDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/users/create`, payload)
      .then((response: any) => {
        resolve(response?.data.data);
      })
      .catch((error: AxiosError) => {
        console.log("error", error);
        reject(error);
      });
  });
}

export function updateUserProfile(id: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/users/${id}/update`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function changeUserPassword(payload: IChangePasswordDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/auth/changePassword`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function resendUserInvite(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/users/${id}/resend-invite`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function deleteUser(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/users/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function forgotPassword(email: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/auth/forgot-password`, { email: email })
      .then((response: any) => {
        resolve(response.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function resetPassword(payload: IResetPasswordDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/auth/reset-password`, payload)
      .then((response: any) => {
        console.log("response", response);
        resolve(response.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// End requests for users. 

export function getGooglePage(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    testApi
      .get(`https://dummy.restapiexample.com/api/v1/employees`)
      .then((response: any) => {
        console.log("google response", response)
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

// Requests for accounts
export function getAccounts(
  query = "",
  companyId?: string,
  limit?: number,
  type?: ACCOUNT_TYPES,
  agentId?: string,
  from?: string,
  to?: string,
  page?: number,
  branchId?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/accounts?search=${query}&company_id=${companyId}&limit=${limit}&type=${type}&user_id=${agentId}&from=${from}&to=${to}&page=${page}&branch_id=${branchId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getAccountById(accountId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/accounts/${accountId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createAccount(payload: ICreateAccountDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/accounts/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        console.log("error", error);
        reject(error);
      });
  });
}

export function updateAccount(id: string, payload: IUpdateAccountDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/accounts/${id}`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        console.log("error", error);
        reject(error);
      });
  });
}

export function getAccountTransactions(accountId: string, from?: string, to?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/accounts/${accountId}/transactions?from=${from}&to=${to}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getAccountStatement(accountId: string, startDate?: string, endDate?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/accounts/${accountId}/statement?from=${startDate}&to=${endDate}&format=${STATEMENT_FORMAT.VIEW}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function downloadStatement(accountId: string, startDate?: string, endDate?: string, format?: STATEMENT_FORMAT): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/accounts/${accountId}/statement?from=${startDate}&to=${endDate}&format=${format}`, {
        responseType: 'blob'
      })
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function assignAccountManager(id: string, userId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/accounts/${id}/assign-manager?user_id=${userId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function completeSusuCycle(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`susu-cycles/${id}/complete`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function openSusuCycle(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`susu-cycles/${id}/open`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}
// End requests for accounts

// Request for customers
export function getCustomers(
  query = "", limit?: number,
  type?: CUSTOMER_TYPES,
  agentId?: string,
  from?: string,
  to?: string,
  page?: number,
  branchId?: string,
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/customers?search=${query}&limit=${limit}&type=${type}&user_id=${agentId}&from=${from}&to=${to}&page=${page}&branch_id=${branchId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getCustomerById(customerId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/customers/${customerId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getCustomerTransactions(customerId: string, query = "", limit = 25): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/customers/${customerId}/transactions?search=${query}&limit=${limit}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function AddCustomer(payload: ICreateCustomerDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/customers/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function updateCustomer(id: string, payload: ICreateCustomerDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/customers/${id}`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function assignCustomerManager(id: string, userId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/customers/${id}/assign-manager?user_id=${userId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}
// End request for customers

// Start requests for branches
export function getBranches(companyId = ""): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/branches?company_id=${companyId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getBranchById(id: string): Promise<IBranch> {
  return new Promise<IBranch>((resolve, reject) => {
    api
      .get(`/branches/${id}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getBranchActivitySummary(id: string): Promise<IBranch> {
  return new Promise<IBranch>((resolve, reject) => {
    api
      .get(`/branches/${id}/activity-summary`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createBranch(payload: ICreateBranchDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/branches/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function updateBranch(id: string, payload: ICreateBranchDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/branches/${id}`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function assignBranchManager(id: string, userId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/branches/${id}/assign-manager?user_id=${userId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

// End requests for branches
export function getTransactions(
  query = "",
  limit = 25,
  type?: TRANSACTION_TYPES,
  agentId?: string,
  from?: string,
  to?: string,
  mode?: PAYMENT_MODES,
  page?: number,
  branchId?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/transactions?search=${query}&limit=${limit}&type=${type}&user_id=${agentId}&from=${from}&to=${to}&mode=${mode}&page=${page}&branch_id=${branchId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getDeposits(limit = 25): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/transactions?type=${TRANSACTION_TYPES.CREDIT}&limit=${limit}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getWithdrawals(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/transactions?type=${TRANSACTION_TYPES.DEBIT}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function CreateTransaction(
  payload: ICreateTransactionDTO
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`accounts/${payload.account_id}/transactions/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function approveTransaction(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`transactions/${id}/approve`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function CreateReconciliationBatch(
  payload: ICreateReconciliationDTO
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`reconciliations/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function approveReconciliationBatch(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/reconciliations/${id}/approve`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}
// Start requests for transactions

// End requests for transactions

// Start requests for uploads
export function uploadUserAvatar(data: FormData): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/users/avatar`, data)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => { });
  });
}

export function uploadCustomerPicture(id: string, data: FormData): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/customers/${id}/picture`, data)
      .then((response: any) => {
        resolve(response.data.data);
      })
      .catch((error: Error) => { });
  });
}

export function uploadPersonPicture(id: string, data: FormData): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/people/${id}/picture`, data)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => { });
  });
}

export function uploadPicture(id: string, data: FormData, url: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(url, data)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => { });
  });
}

export function uploadDocument(url: string, data: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`${url}`, data)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => { });
  });
}

export function uploadDocumentByPut(url: string, data: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`${url}`, data)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => { });
  });
}

export function deleteUploadedImage(url: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .delete(`${url}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => { });
  });
}
// End requests for uploads

// Start requests for main dashboard
export function getMainMetrics(from?: string, to?: string): Promise<IDashboardMainMetrics> {
  return new Promise<IDashboardMainMetrics>((resolve, reject) => {
    api
      .get(`dashboard/metrics?from=${from}&to=${to}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getGeneralMetrics(from?: string, to?: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`dashboard/generalMetrics?from=${from}&to=${to}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => { });
  });
}

export function getMonthlyPerformance(from?: string, to?: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`dashboard/monthly-performance?from=${from}&to=${to}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => { });
  });
}

export function getRevenueSummary(from?: string, to?: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`dashboard/revenue-summary?from=${from}&to=${to}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => { });
  });
}

export function getAgentsLeaderboard(rankBy: RANK_AGENTS_BY, period: string, branchId?: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`dashboard/agents-leaderboard?rank_by=${rankBy}&period=${period}&branch_id=${branchId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => { });
  });
}
// End requests for main dashboard

// Start requests for companies. 
export function getCompanies(query = ""): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/companies?search=${query}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getCompanyById(companyId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/companies/${companyId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createCompany(payload: ICreateCompanyFormData): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/companies/create`, payload)
      .then((response: any) => {
        resolve(response?.data.data);
      })
      .catch((error: AxiosError) => {
        console.log("error", error);
        reject(error);
      });
  });
}

export function updateCompany(id: string, payload: IUpdateCompanyDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/companies/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data.data);
      })
      .catch((error: AxiosError) => {
        console.log("error", error);
        reject(error);
      });
  });
}

export function activateCompany(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/companies/activate`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function getCompanyAlerts(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/companies/alerts`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}
// End requests for companies
export function updateCustomerPerson(
  id: string,
  payload: ICreateCustomerPersonDTO
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/customer-people/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function createCustomerPerson(
  id: string,
  payload: ICreateCustomerPersonDTO
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/customers/${id}/people/create`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}
// Start requests for people

// End requests for people

// Start requests for loans
export function getFinancingOffers({ query, type }:
  {
    query?: string,
    type?: FINANCING_TYPES
  }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/financing/offers?search=${query}&type=${type}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createFinancingOffer(payload: ICreateFinancingOfferDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/financing/offers/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function updateFinancingOffer(id: string, payload: ICreateFinancingOfferDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/financing/offers/${id}`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function createHirePurchase(payload: ICreateHirePurchaseDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/financing/hire-purchase/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function updateHirePurchase(id: string, payload: ICreateHirePurchaseDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/financing/${id}`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getHirePurchases(query?: string, companyId?: string, agentId?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/hire-purchases?search=${query}&company_id=${companyId}&user_id=${agentId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createFinancing(payload: ICreateFinancingDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/financing/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function updateFinancing(id: string, payload: ICreateFinancingDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/financing/${id}`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getFinancingAgreements({ query, companyId, agentId, type }:
  {
    query?: string,
    companyId?: string,
    agentId?: string;
    type?: FINANCING_TYPES
  }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/financing?search=${query}&company_id=${companyId}&user_id=${agentId}&type=${type}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getFinancingAgreementById(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/financing/${id}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function assignFinancingManager(id: string, userId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/financing/${id}/assign-manager?user_id=${userId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function approveFinancing(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/financing/${id}/approve`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function generateFinancingAgreement(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/financing/${id}/generate-agreement`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function previewFinancingAgreement(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/financing/${id}/preview-agreement`, {
        responseType: 'blob'
      })
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function disburseFinancing(id: string, payload: { disbursed_date: string }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/financing/${id}/disburse`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function generateFinancingSchedules(id: string, payload: { start_date: string }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/financing/${id}/generate-schedules`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function payFinancingSchedule(id: string, scheduleId: string, payload: ICreateFinancingPaymentDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/financing/${id}/schedules/${scheduleId}/pay`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createFinancingPayment(id: string, payload: ICreateFinancingPaymentDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/financing/${id}/pay`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getLoanProducts(query?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/loan-products?search=${query}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createLoanProduct(payload: ICreateLoanProductDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/loan-products/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function updateLoanProduct(id: string, payload: ICreateLoanProductDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/loan-products/${id}`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function getLoans(query?: string, companyId?: string, agentId?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/loans?search=${query}&company_id=${companyId}&user_id=${agentId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function updateLoan(id: string, payload: ICreateLoanDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/loans/${id}`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createLoan(payload: ICreateLoanDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/loans/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function calculateFinancing(payload: ICreateFinancingDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/financing/calculate`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getLoanById(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/loans/${id}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createLoanPayment(id: string, payload: ICreateFinancingPaymentDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/loans/${id}/pay`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function payLoanSchedule(id: string, scheduleId: string, payload: ICreateFinancingPaymentDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/loans/${id}/schedules/${scheduleId}/pay`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function approveLoan(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/loans/${id}/approve`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function disburseLoan(id: string, payload: { disbursed_date: string }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/loans/${id}/disburse`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function assignLoanManager(id: string, userId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/loans/${id}/assign-manager?user_id=${userId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}
// End requests for loans

// Start requests for passbook sales

export function getPassbookSales(query?: string, companyId = "", agentId?: string, status?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/passbooks?search=${query}&company_id=${companyId}&user_id=${agentId}&status=${status}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createPassbookSale(payload: ICreatePassbookSaleDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/passbooks/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response);
      });
  });
}

// End requests for passbook sales

// Start requests for revenues
export function getRevenues(query?: string, companyId = "", agentId?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/revenues?search=${query}&company_id=${companyId}&user_id=${agentId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function recordRevenue(payload: IRecordRevenueDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/revenues/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response);
      });
  });
}
// End requests for revenues

// Start requests for agent targets
export function setAgentTarget(payload: ISetAgentTargetDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/agent-targets/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function updateAgentTarget(id: string, payload: ISetAgentTargetDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/agent-targets/${id}`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}
// End requests for agent targets

// Start requests for payments
export function initializeCompanyPayment(payload: IInitializeCompanyPaymentDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/payments/initialize`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function verifyPaymentRequest(payload: IVerifyPaymentDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/payments/verify`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}
// End requests for payments

// Start requests for billing and credits
export function getCredits(query?: string, companyId = "", branchId?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/companies/credits?search=${query}&company_id=${companyId}&branch_id=${branchId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getPayments(query?: string, companyId = "", branchId?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/companies/payments?search=${query}&company_id=${companyId}&branch_id=${branchId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getCreditsSummary(query?: string, companyId = "", branchId?: string, from?: string, to?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/companies/credits/summaryActivity?search=${query}&company_id=${companyId}&branch_id=${branchId}&from=${from}&to=${to}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getPaymentMetrics(companyId = "", branchId?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/companies/payments/metrics?&company_id=${companyId}&branch_id=${branchId}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getPaymentLink(payload: IGetPaymentLinkDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/payments/getLink`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function getPaymentPage(payload: IGetPaymentLinkDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/payments/getPage`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}
// End requests for billing and credits

// Requests for reports
export function downloadReport(reportType: REPORT_TYPES, from: string, to?: string, format?: DOWNLOAD_FILE_TYPES): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/reports?type=${reportType}&format=${format}&from=${from}&to=${to}`, {
        responseType: 'blob'
      })
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}
// End requests for reports

// Start requests for products
export function getProducts({ query, companyId, allowHirePurchase }:
  {
    query?: string,
    companyId?: string,
    allowHirePurchase?: boolean
  }
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/products?search=${query}&company_id=${companyId}&allow_hire_purchase=${allowHirePurchase}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function createProduct(payload: ICreateProductDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/products/create`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function updateProduct(id: string, payload: ICreateProductDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/products/${id}`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function getProductById(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/products/${id}`)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}
// End requests for products

export function submitContact(payload: IContactDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/contact`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}


